import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import PageHeader from '../components/PageHeader';

const WatokSecurityPolicyPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const sections = useMemo(() => t('watok_policy.sections', { returnObjects: true }), [t]);

  return (
    <div className="relative min-h-screen bg-transparent font-inter">
      <PageHeader hideCTA />

      <main className="pt-32 pb-20 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <button
            type="button"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/watok'))}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[#141414] mb-8 transition font-medium"
          >
            <ArrowLeft size={16} />
            {t('common.back')}
          </button>

          <section className="bg-white border border-gray-200 rounded-3xl p-8 md:p-12 mb-10">
            <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 px-4 py-2 rounded-full text-sm font-semibold mb-6 border border-emerald-100">
              <ShieldCheck size={16} />
              {t('watok_policy.badge')}
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#141414] mb-4">
              {t('watok_policy.title')}
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed max-w-3xl">
              {t('watok_policy.subtitle')}
            </p>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
              {t('watok_policy.summary', { returnObjects: true }).map((item) => (
                <div key={item} className="bg-gray-50 border border-gray-200 rounded-2xl p-5 text-gray-700 font-medium">
                  {item}
                </div>
              ))}
            </div>
          </section>

          <section className="space-y-6 mb-12">
            {sections.map((section) => (
              <div key={section.title} className="bg-white border border-gray-200 rounded-3xl p-8 md:p-10">
                <h2 className="text-2xl font-bold text-[#141414] mb-4">{section.title}</h2>
                {section.description ? (
                  <p className="text-gray-600 leading-relaxed mb-5">{section.description}</p>
                ) : null}
                <div className="space-y-3">
                  {section.items.map((item) => (
                    <div key={item} className="flex items-start gap-3 text-gray-700">
                      <Check size={18} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                      <span className="leading-relaxed">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </section>

          <section className="bg-[#141414] text-white rounded-3xl p-8 md:p-10 border border-white/10">
            <h2 className="text-2xl font-bold mb-3">{t('watok_policy.footer_title')}</h2>
            <p className="text-white/80 leading-relaxed mb-6 max-w-3xl">
              {t('watok_policy.footer_desc')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/watok"
                className="bg-white text-[#141414] hover:bg-gray-100 font-bold py-3 px-8 rounded-full transition text-center"
              >
                {t('watok_policy.footer_primary')}
              </Link>
              <Link
                to="/apps"
                className="bg-white/10 border border-white/15 text-white hover:bg-white/15 font-bold py-3 px-8 rounded-full transition text-center"
              >
                {t('watok_policy.footer_secondary')}
              </Link>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default WatokSecurityPolicyPage;
